import React, { useState, useEffect, useCallback } from 'react';
import { formatIngredientName } from '../utils/ingredientFormat';
import { API_URL } from '../config/api';

function ShoppingPage({ token }) {
  const [shoppingList, setShoppingList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const menuId = localStorage.getItem('menuId') || '';

  const fetchList = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shopping/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) {
        setShoppingList(data.list);
        setMessage('');
      } else {
        if (data.error === 'List not found') {
          localStorage.removeItem('shoppingListId');
          setShoppingList(null);
          return;
        }
        setMessage(data.error || 'Error al cargar la lista');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
    setLoading(false);
  }, [token]);

  const generateList = useCallback(async () => {
    if (!menuId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/shopping/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ menuId })
      });
      const data = await res.json();
      if (data.ok && data.shoppingList?._id) {
        setShoppingList(data.shoppingList);
        localStorage.setItem('shoppingListId', data.shoppingList._id);
        setMessage('');
      } else {
        setMessage(data.error || 'Error al generar la lista');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
    setLoading(false);
  }, [menuId, token]);

  useEffect(() => {
    if (!menuId) return;
    // Recalcula lista al entrar para considerar stock/menu actualizados.
    generateList();
  }, [menuId, generateList]);

  const updateQuantity = async (idx, newQty) => {
    if (!shoppingList?._id) return;
    try {
      const res = await fetch(`${API_URL}/shopping/${shoppingList._id}/item/${idx}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ purchased: newQty })
      });
      if (res.ok) {
        fetchList(shoppingList._id);
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const toggleCheck = async (idx) => {
    if (!shoppingList?._id) return;
    try {
      const res = await fetch(`${API_URL}/shopping/${shoppingList._id}/item/${idx}/check`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchList(shoppingList._id);
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  const loadToStock = async () => {
    if (!shoppingList?._id) return;
    try {
      const res = await fetch(`${API_URL}/shopping/${shoppingList._id}/load-to-stock`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) {
        generateList();
        setMessage('Ingredientes cargados al stock');
      } else {
        setMessage(data.error || 'Error al cargar al stock');
      }
    } catch (err) {
      setMessage('Error: ' + err.message);
    }
  };

  return (
    <div className="page shopping-page">
      <h2>Lista de Compras</h2>

      {message && <p className={message.startsWith('Error') ? 'error' : 'success'}>{message}</p>}
      {loading && <p>Cargando...</p>}

      {!menuId && (
        <div className="alert">
          <p>Generá un menú primero para crear la lista de compras.</p>
        </div>
      )}

      {menuId && (
        <button onClick={generateList} className="btn-secondary" style={{ marginBottom: '12px' }}>
          Recalcular según stock actual
        </button>
      )}

      {shoppingList && (
        <div className="shopping-list">
          {shoppingList.items && shoppingList.items.length > 0 ? (
            <>
              <div className="items-container">
                {shoppingList.items.map((item, idx) => (
                  <div key={`${item.name}-${idx}`} className={`shopping-item ${item.checked ? 'checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={() => toggleCheck(idx)}
                    />
                    <div className="item-info">
                      <span className="item-name">{formatIngredientName(item.name)}</span>
                      <span className="item-qty">
                        Necesarios: {item.required} {item.unit}
                      </span>
                    </div>
                    <div className="item-controls">
                      <input
                        type="number"
                        value={item.purchased}
                        onChange={(e) => updateQuantity(idx, parseFloat(e.target.value))}
                        min="0"
                        step="0.1"
                        disabled={item.checked}
                      />
                      <span>{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={loadToStock} className="btn-primary">
                Cargar seleccionados al stock
              </button>
            </>
          ) : (
            <p>No hay ítems para comprar.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default ShoppingPage;
